import { useContext, useEffect, useState } from "react"
import AppContext from "../services/context.js"
import { useLazyQuery } from "@apollo/client";
import { GET_ITEM } from "../services/api.js";
import { Link } from 'react-router-dom';
import { useParams, useNavigate } from 'react-router-dom';
import PageTemplate from "../components/PageTemplate.jsx";
import EvidenceTooltip from "../components/evidence/EvidenceTooltip.jsx";
import EvidencePanelModal from "../components/evidence/EvidencePanelModal.jsx";
import SanitizedHtml from "../components/SanitizedHtml.jsx";
import SearchResult from "../components/SearchResult.jsx";
import clsx from 'clsx';
import { buildPrescriptionText } from "../utils/prescriptionText.js";
import { trackEvent } from "../lib/analytics.js";
import { usePageTitle } from "../hooks/usePageTitle.js";
import { useToast } from "../hooks/useToast.js";
import { useError } from "../hooks/useError.js";
import { getPrimaryAssessmentColor } from "../utils/assessmentPresentation.js";

const Content = () => {
  const { showError } = useError();
  const { individualResult: r, setIndividualResult, searchResults, searchTerm, loading, themeKey } = useContext(AppContext),
    [getIndividualResult, { data }] = useLazyQuery(GET_ITEM, {
      onError: (error) => showError(error, { fallback: 'Falha ao carregar o conteúdo.' }),
    }),
    { type: urlParamType, id: urlParamId } = useParams(),
    navigate = useNavigate();

  const toast = useToast();
  const isDarkTheme = themeKey === 'dark';
  const [evidencePanel, setEvidencePanel] = useState(null);
  usePageTitle(r.title || null);

  const openEvidencePanel = (itemType, item) => {
    setEvidencePanel({
      itemType,
      relationName: itemType === 'metabolite' ? item.name : item.term,
      relationDescription: itemType === 'metabolite' ? item.description : item.meaning,
      assessments: item.assessments || [],
      relevance: item.relevance || null,
      relationshipComment: item.comment || '',
    });
  };

  const handleItemClick = (q) => {
    const [type, id] = q.split(":");
    return `/${type}/${id}`;
  };

  const COPY_SUCCESS_MESSAGE = 'A prescrição foi copiada para área de transferência';

  const handleCopyToClipboard = async (value) => {
    const text = typeof value === 'string' ? value : value?.target?.textContent;

    if (!text) {
      return null;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(COPY_SUCCESS_MESSAGE);
      } else {
        throw new Error('Clipboard API indisponível');
      }
    } catch (copyError) {
      toast.error('Não foi possível copiar a prescrição.');
    }

    return text;
  };

  const handlePrescriptionSuggestionClick = (suggestion, fallbackText) => {
    const prescriptionText = buildPrescriptionText(suggestion);

    handleCopyToClipboard(prescriptionText || fallbackText);
  };

  useEffect(() => {
    if (data) {
      setIndividualResult(data.getItem)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    getIndividualResult({ variables: { q: `${urlParamType}:${urlParamId}` } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (urlParamId) {
      getIndividualResult({ variables: { q: `${urlParamType}:${urlParamId}` } }); // Refetch data when URL params change
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParamId, urlParamType]);

  return <PageTemplate>
    {searchResults.length > 0 && searchTerm !== "" && !loading ? <SearchResult /> : (
      <main className="content-main">
        <section className="px-4 py-8 max-w-[900px] mx-auto">
          {r.title && <header>
            <h1 className="flex items-center gap-2">
              {r.title} 
              <button 
                onClick={async () => {
                  trackEvent('share', {
                    method: 'clipboard',
                    content_type: urlParamType,
                    content_id: `${urlParamType}:${urlParamId}`,
                  })
                  try {
                    await navigator.clipboard.writeText(window.location.href)
                    toast.success("Link copiado")
                  } catch (copyError) {
                    toast.error("Não foi possível copiar o link.")
                  }
                }}
                className={clsx(
                  "inline-flex items-center text-base px-4 py-2 rounded-lg outline-none cursor-pointer",
                  isDarkTheme ? [
                    "bg-greenbook-dark-bg text-greenbook-dark-text border border-greenbook-dark-primary",
                    "hover:bg-greenbook-dark-primary hover:text-white"
                  ] : [
                    "bg-greenbook-light-bg text-greenbook-light-text border border-gray-300",
                    "hover:bg-green-700 hover:text-white"
                  ]
                )}
              >
                <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2">
                  <path d="M15 14.08C14.24 14.08 13.56 14.38 13.04 14.85L5.91 10.7C5.96 10.47 6 10.24 6 10C6 9.76 5.96 9.53 5.91 9.3L12.96 5.19C13.5 5.69 14.21 6 15 6C16.66 6 18 4.66 18 3C18 1.34 16.66 0 15 0C13.34 0 12 1.34 12 3C12 3.24 12.04 3.47 12.09 3.7L5.04 7.81C4.5 7.31 3.79 7 3 7C1.34 7 0 8.34 0 10C0 11.66 1.34 13 3 13C3.79 13 4.5 12.69 5.04 12.19L12.16 16.35C12.11 16.56 12.08 16.78 12.08 17C12.08 18.61 13.39 19.92 15 19.92C16.61 19.92 17.92 18.61 17.92 17C17.92 15.39 16.61 14.08 15 14.08Z" fill="currentColor" />
                </svg> 
                Compartilhar
              </button>
            </h1>
            {Boolean(r.categories?.length) && <p>Categorias: {r.categories.map((elem) => elem.name).join(", ")}</p>}
            {Boolean(r.family?.trim()) && <p>Família botânica: {r.family.trim()}</p>}
            {Boolean(r.synonyms?.length) && <p>Sinonímias: {(r.synonyms || []).filter(Boolean).join(", ")}</p>}
          </header>}
        </section>
        
        {Boolean(r.species?.length) && (
          <section className="px-4 py-8 max-w-[900px] mx-auto">
            <header>
              <h2>Especies</h2>
            </header>
            <div className="flex flex-wrap gap-2">
              {r.species.map((elem) => (
                <Link to={handleItemClick(elem.q)} key={elem.name}>
                  <button className={clsx(
                    "px-4 py-2 rounded-lg mr-1 outline-none cursor-pointer",
                    isDarkTheme ? [
                      "bg-greenbook-dark-bg text-greenbook-dark-text border border-greenbook-dark-primary",
                      "hover:bg-greenbook-dark-primary hover:text-white"
                    ] : [
                      "bg-greenbook-light-bg text-greenbook-light-text border border-gray-300",
                      "hover:bg-green-700 hover:text-white"
                    ]
                  )}>
                    {elem.name}
                  </button>
                </Link>
              ))}
            </div>
          </section>
        )}
        
        {Boolean(r.commonNames?.length) && (
          <section className="px-4 py-8 max-w-[900px] mx-auto">
            <header>
              <h2>Nomes Comuns</h2>
            </header>
            <div className="flex flex-wrap gap-2">
              {r.commonNames.map((elem) => (
                <Link to={handleItemClick(elem.q)} key={elem.id}>
                  <button className={clsx(
                    "px-4 py-2 rounded-lg mr-1 outline-none cursor-pointer",
                    isDarkTheme ? [
                      "bg-greenbook-dark-bg text-greenbook-dark-text border border-greenbook-dark-primary",
                      "hover:bg-greenbook-dark-primary hover:text-white"
                    ] : [
                      "bg-greenbook-light-bg text-greenbook-light-text border border-gray-300",
                      "hover:bg-green-700 hover:text-white"
                    ]
                  )}>
                    {elem.name}
                  </button>
                </Link>
              ))}
            </div>
          </section>
        )}
        
        {Boolean(r.metabolites?.length) && (
          <section className="px-4 py-8 max-w-[900px] mx-auto">
            <header>
              <h2>Metabólitos</h2>
            </header>
            <div className="flex flex-wrap gap-2">
              {r.metabolites.map((elem) => (
                <Link to={handleItemClick(elem.q)} key={elem.name}>
                  <EvidenceTooltip
                    title={elem.name}
                    description={elem.description}
                    itemType="metabolite"
                    relevance={elem.relevance}
                    assessments={elem.assessments}
                    onOpenDetails={() => openEvidencePanel('metabolite', elem)}
                  >
                    <button
                      className={clsx(
                        "px-4 py-2 rounded-lg mr-1 outline-none cursor-pointer border-2",
                        isDarkTheme ? [
                          "bg-greenbook-dark-bg text-greenbook-dark-text",
                          "hover:bg-greenbook-dark-primary hover:text-white"
                        ] : [
                          "bg-greenbook-light-bg text-greenbook-light-text",
                          "hover:bg-green-700 hover:text-white"
                        ]
                      )}
                      style={{
                        borderColor: getPrimaryAssessmentColor(
                          elem.assessments,
                          elem.relevance,
                          'metabolite',
                        ),
                      }}
                    >
                      {elem.name}
                    </button>
                  </EvidenceTooltip>
                </Link>
              ))}
            </div>
          </section>
        )}

        {Boolean(r.therapeuticEffects?.length) && (
          <section className="px-4 py-8 max-w-[900px] mx-auto">
            <header>
              <h2>Efeitos Terapêuticos</h2>
            </header>
            <div className="flex flex-wrap gap-2">
              {r.therapeuticEffects.map((elem) => (
                <Link to={handleItemClick(elem.q)} key={`${elem.q}:${elem.comment || ""}`}>
                  <EvidenceTooltip
                    title={elem.term}
                    description={elem.meaning}
                    itemType="therapeuticEffect"
                    relevance={elem.relevance}
                    assessments={elem.assessments}
                    onOpenDetails={() => openEvidencePanel('therapeuticEffect', elem)}
                  >
                    <button
                      className={clsx(
                        "px-4 py-2 rounded-lg mr-1 outline-none cursor-pointer border-2",
                        isDarkTheme ? [
                          "bg-greenbook-dark-bg text-greenbook-dark-text",
                          "hover:bg-greenbook-dark-primary hover:text-white"
                        ] : [
                          "bg-greenbook-light-bg text-greenbook-light-text",
                          "hover:bg-green-700 hover:text-white"
                        ]
                      )}
                      style={{
                        borderColor: getPrimaryAssessmentColor(
                          elem.assessments,
                          elem.relevance,
                          'therapeuticEffect',
                        ),
                      }}
                    >
                      {elem.comment ? `${elem.term} (${elem.comment})` : elem.term}
                    </button>
                  </EvidenceTooltip>
                </Link>
              ))}
            </div>
          </section>
        )}
        
        {r.description && (
          <section className="px-4 py-8 max-w-[900px] mx-auto">
            <details className="cursor-pointer">
              <summary className="font-semibold">Notas de Aula</summary>
              <SanitizedHtml
                html={r.description}
                className="text-sm leading-5 mt-2 lesson-notes"
              />
            </details>
          </section>
        )}
        
        {Boolean(r.prescriptionSuggestions?.length) && (
          <section className="px-4 py-8 max-w-[900px] mx-auto">
            <header>
              <h2>Sugestões de Prescrição</h2>
            </header>
            <i>Clique em uma das prescrições abaixo para copiar:</i>
            <ul className="mt-4">
              {r.prescriptionSuggestions.map((elem, index) => {
                const specieName = elem.specie?.name ?? '';
                const partName = elem.part?.name ? `(${elem.part.name})` : '';
                const buttonLabel = [specieName, partName, elem.dosage].filter(Boolean).join(' ');

                return (
                  <li key={index} className="list-none my-2">
                    <button
                      onClick={() => handlePrescriptionSuggestionClick(elem, buttonLabel)}
                      className={clsx(
                        "inline px-4 py-2 rounded-lg mr-1 outline-none cursor-pointer",
                        isDarkTheme ? [
                          "bg-greenbook-dark-bg text-greenbook-dark-text border border-greenbook-dark-primary",
                          "hover:bg-greenbook-dark-primary hover:text-white"
                        ] : [
                          "bg-greenbook-light-bg text-greenbook-light-text border border-gray-300",
                          "hover:bg-green-700 hover:text-white"
                        ]
                      )}
                    >
                      {buttonLabel}
                    </button>
                    {(elem.description || elem.quantity) && (
                      <p className="inline ml-2">
                        {elem.quantity && <span>{elem.quantity}</span>}
                        {elem.description && elem.quantity && <span> - </span>}
                        {elem.description && <span>{elem.description}</span>}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        
        {Boolean(r.pharmaceuticalProducts?.length) && (
          <section className="px-4 py-8 max-w-[900px] mx-auto">
            <header>
              <h2>Produtos farmacêuticos relacionados</h2>
            </header>
            <ul className="mt-4">
              {r.pharmaceuticalProducts.map((p) => (
                <li key={p.id} className="list-none my-2">
                  <details className="cursor-pointer">
                    <summary className="font-semibold">
                      {p.commercialName} ({p.substance}) - {p.pharmaceuticalForm.map(f => f.name).join(', ')}
                    </summary>
                    <div className="ml-4 mt-2">
                      {Boolean(p.presentations.length) && (
                        <div>
                          <strong>Apresentações:</strong>
                          <ul className="ml-4">
                            {Object.entries(
                              p.presentations.reduce((acc, pr) => {
                                if (!acc[pr.concentration]) acc[pr.concentration] = [];
                                if (pr.quantity) acc[pr.concentration].push(pr.quantity);
                                return acc;
                              }, {})
                            ).map(([concentration, quantities]) => (
                              <li key={concentration} className="list-disc">
                                {concentration} ({quantities.join(', ')})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {p.metadata && (() => {
                        let data;
                        try {
                          data = JSON.parse(p.metadata);
                        } catch {
                          data = null;
                        }
                        return data ? (
                          <div className="mt-2">
                            <strong>Outras informações:</strong>
                            <p>
                              {data.Empresa && `Empresa: ${data.Empresa}, `}
                              {data.CNPJ && `CNPJ ${data.CNPJ}`} {data.Site && (
                                <a 
                                  href={data.Site} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className={clsx(
                                    "underline",
                                    isDarkTheme ? "text-greenbook-dark-primary" : "text-greenbook-light-primary"
                                  )}
                                >
                                  Site
                                </a>
                              )}
                            </p>
                          </div>
                        ) : <pre className="mt-2">{p.metadata}</pre>;
                      })()}
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          </section>
        )}
        
        {Boolean(r.references?.length) && (
          <section className="px-4 py-8 max-w-[900px] mx-auto">
            <header>
              <h2>Referências</h2>
            </header>
            <ul className="mt-4">
              {r.references.map((reference) => (
                <li key={reference.id} className="list-none my-4">
                  <p>
                    {reference.authors}
                    {reference.year && ` (${reference.year})`}
                  </p>
                  <p>{reference.title}</p>
                  {reference.journal && <p>{reference.journal}</p>}
                  {reference.doi && (() => {
                    const url = reference.doi.startsWith('http') ? reference.doi : `https://doi.org/${reference.doi}`;
                    return (
                      <p>
                        <a 
                          href={url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={clsx(
                            "underline",
                            isDarkTheme ? "text-greenbook-dark-primary" : "text-greenbook-light-primary"
                          )}
                        >
                          DOI
                        </a>
                      </p>
                    );
                  })()}
                </li>
              ))}
            </ul>
          </section>
        )}
        
        <section className="text-center mt-4 pb-8">
          <button 
            onClick={() => {
              setIndividualResult({})
              navigate(-1)
            }}
            className={clsx(
              "px-4 py-2 rounded-lg outline-none cursor-pointer",
              isDarkTheme ? [
                "bg-greenbook-dark-bg text-greenbook-dark-text border border-greenbook-dark-primary",
                "hover:bg-greenbook-dark-primary hover:text-white"
              ] : [
                "bg-greenbook-light-bg text-greenbook-light-text border border-gray-300",
                "hover:bg-green-700 hover:text-white"
              ]
            )}
          >
            Voltar
          </button>
        </section>
      </main>
    )}
    <EvidencePanelModal
      isOpen={Boolean(evidencePanel)}
      onClose={() => setEvidencePanel(null)}
      specieName={r.title || ''}
      relationName={evidencePanel?.relationName || ''}
      relationDescription={evidencePanel?.relationDescription || ''}
      itemType={evidencePanel?.itemType || 'therapeuticEffect'}
      assessments={evidencePanel?.assessments || []}
      relevance={evidencePanel?.relevance || null}
      relationshipComment={evidencePanel?.relationshipComment || ''}
    />
  </PageTemplate>
}

export default Content
